import React from 'react';

import { Avatar, Box, Typography } from '@mui/material';

import {
  IBookingsRequested,
  convertToDecimals,
  currency,
  fallbackText,
  pharmacyActions,
  professionalPicturesList,
} from '@pharmaplan/common';
import { ReactComponent as RatingSmall } from '@pharmaplan/common/assets/icons/ratingSmall.svg';
import { ReactComponent as Location } from '@pharmaplan/common/assets/icons/locationSmall.svg';
import AvatarFallback from '@pharmaplan/common/assets/icons/avatarPlaceholder.png';
import { ReactComponent as Ticker } from '../../../../assets/svg/customTicker.svg';

import getLongTermContractLabels from '../../../common/LongTermContractLabels/LongTermContractLabels';
import { ReactComponent as FavouriteSmall } from '../../../../assets/svg/favIconWithBorder.svg';
import { ScreenTypes, SkeletonVariants } from '../../../../helpers/Constants';
import { approvalTime, getDayMonthDateYear } from '../../../Reports/helpers';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { renderScreen } from '../../../../actions/drawerActions';
import CounterOfferChip from '../../../common/CounterOfferChip';
import genericClasses from '../../../../theme/GenericClasses';
import ThemeConstants from '../../../../theme/ThemeConstants';
import { IBookingRequestsList } from '../BookingRequestsList';
import WithSkeleton from '../../../common/WithSkeleton';
import strings from '../../../../localization';

import classes from './styles';
import { useAppSelector } from '../../../../hooks/useAppSelector';

interface IBookingRequest extends IBookingRequestsList {
  event: IBookingsRequested;
  preventInteraction?: boolean;
}

const BookingRequest: React.FC<IBookingRequest> = ({
  event,
  date,
  type,
  eventId,
  eventIds,
  preventInteraction,
}) => {
  const dispatch = useAppDispatch();

  const {
    counterContainer,
    container,
    content,
    counterOfferPrice,
    rowContainer,
    titleContainer,
    titleMargin,
    ratingText,
    nameText,
    subtitle,
    subtitleContainer,
    avatar,
    avatarContainer,
    favouriteContainer,
    offerPrice,
    locationContainer,
    confirmContainer,
    label,
    subContainer,
    zeroMargin,
  } = classes;

  const {
    counterOfferId,
    counterPrice,
    price,
    isFavorite,
    rating,
    requestedDate,
    distance = '',
    city,
    bookingId,
    name,
    bookingStateExpiration,
    requestEndDate,
    requestStartDate,
    longTermContractId,
    shiftCount,
    requestCount,
    professionalId,
    counterShiftContractId,
  } = event ?? {};

  const isLongTerm = !!longTermContractId;
  const displayPrice = counterPrice || price;
  const displayRating = ((rating * 5) / 100).toPrecision(2);
  const convertedPrice = `${currency}${convertToDecimals(price)}`;
  const professionalPictures = useAppSelector(professionalPicturesList);

  const confirmTime = approvalTime(bookingStateExpiration ?? '');
  const confirmDate = `${confirmTime} EST`;
  const confirmBefore = `${strings.formatString(
    strings.confirmBefore,
    confirmDate,
  )}`;

  const requestedOn = strings.formatString(
    strings.requestedOn,
    getDayMonthDateYear(requestedDate),
  );

  const requestedCount = strings.formatString(
    strings.requestedCount,
    `${requestCount}/${shiftCount}`,
  );

  const convertedDisplayPrice = ` $${convertToDecimals(
    displayPrice as number,
  )}`;

  const displayDistance = strings.formatString(
    strings.numAway,
    distance ?? fallbackText,
  );

  const SkeletonBox = WithSkeleton({
    width: 270,
    height: 22,
    successAction: pharmacyActions.requestedBookings,
    variant: SkeletonVariants.rectangular,
  })(Box);

  const SkeletonTypography = WithSkeleton({
    successAction: pharmacyActions.requestedBookings,
    variant: SkeletonVariants.rectangular,
  })(Typography);

  const gotoPharmacistProfile = () => {
    dispatch(
      renderScreen({
        eventId: isLongTerm ? eventId : bookingId,
        workshiftId: eventId,
        date,
        eventIds,
        type,
        screenNumber: 3,
        pharmacistId: professionalId,
        screenType: ScreenTypes.workshiftSummary,
        extraParameters: {
          preventInteraction,
          longTermShiftContractId: longTermContractId,
        },
      }),
    );
  };

  const { RangeLabel } = getLongTermContractLabels({
    successAction: pharmacyActions.requestedBookings,
    contractEndDate: requestEndDate,
    contractStartDate: requestStartDate,
    isLongTerm: !!longTermContractId,
  });

  const isCounterOffer = counterOfferId || counterShiftContractId;

  const { image } = professionalPictures.find(
    (item) =>
      item.professionalId === professionalId,
  ) ?? {};
  const avatarSrc = image || AvatarFallback;

  const SkeletonAvatar = WithSkeleton({
    successAction: pharmacyActions.requestedBookings,
    variant: 'circular',
  })(Avatar);

  return (
    <Box sx={counterContainer}>
      {isCounterOffer && <CounterOfferChip />}

      <Box sx={container} onClick={gotoPharmacistProfile}>
        <Box sx={subContainer}>
          <Box sx={avatarContainer}>
            <SkeletonAvatar src={avatarSrc} sx={avatar} />

            {isFavorite && (
              <Box sx={favouriteContainer}>
                <FavouriteSmall />
              </Box>
            )}
          </Box>

          <Box sx={content}>
            <Box sx={titleContainer}>
              <SkeletonTypography sx={nameText}>{name}</SkeletonTypography>
              <Box sx={rowContainer}>
                <RatingSmall style={titleMargin} />
                <Typography sx={ratingText}>{displayRating}</Typography>
              </Box>
            </Box>

            <Box sx={subtitleContainer}>
              <Typography sx={subtitle}>
                {longTermContractId ? requestedCount : requestedOn}
              </Typography>

              <Box sx={rowContainer}>
                {counterOfferId && (
                  <Typography sx={counterOfferPrice}>
                    {convertedPrice}
                  </Typography>
                )}
                <SkeletonTypography sx={offerPrice}>
                  {convertedDisplayPrice}
                </SkeletonTypography>
              </Box>
            </Box>

            {longTermContractId && (
              <Box sx={subtitleContainer}>
                <Box sx={genericClasses.flex}>
                  <RangeLabel sx={zeroMargin} />
                </Box>
                <Typography sx={subtitle}>{displayDistance}</Typography>
              </Box>
            )}

            <Box sx={subtitleContainer}>
              <Box sx={locationContainer}>
                <Location />
                <SkeletonTypography sx={subtitle}>{city}</SkeletonTypography>
              </Box>

              {!longTermContractId && (
                <SkeletonTypography sx={subtitle}>
                  {displayDistance}
                </SkeletonTypography>
              )}
            </Box>
          </Box>
        </Box>
        <SkeletonBox sx={confirmContainer}>
          <Ticker stroke={ThemeConstants.pharmacy.requested} />
          <Typography sx={label}>{confirmBefore}</Typography>
        </SkeletonBox>
      </Box>
    </Box>
  );
};

export default BookingRequest;
